<div class="v-one-card-body">
  <!-- Consecutives and Codes Section (Sales cards only — Cartera renders its
       own consecutives in the header, see CardHeaderComponent) -->
  @if (!isPortfolioCard() && codes().length > 0) {
    <div class="v-one-card-body__codes-section">
      @for (code of codes(); track code.value; let i = $index) {
        <div
          class="v-one-card-body__code-item"
          [class.v-one-card-body__code-item--first]="i === 0"
          [class.v-one-card-body__code-item--middle]="i === 1"
          [class.v-one-card-body__code-item--last]="i === codes().length - 1"
          [class.v-one-card-body__code-item--highlighted]="isHighlighted(code)"
        >
          <span class="v-one-card-body__code-value">
            @if (code.boldPrefix) {
              <b>{{ code.boldPrefix }}</b
              >{{ code.value }}
            } @else {
              {{
                isCufeCode(code) && code.value.length > 13
                  ? code.value.slice(0, 13) + '...'
                  : code.value
              }}
            }
          </span>
          @if (isCufeCode(code)) {
            <button
              class="v-one-card-body__copy-button"
              (click)="copyToClipboard(code.value)"
              type="button"
              [attr.aria-label]="'Copy CUFE ' + code.value"
            >
              <v-one-icon name="copy" />
            </button>
          }
        </div>
      }
      <div class="v-one-card-body__divider"></div>
    </div>
  }

  <!-- Client Information -->
  @if (client()) {
    <div class="v-one-card-body__section">
      <span class="v-one-card-body__label">{{
        clientLabelKey() | translate
      }}</span>
      <span class="v-one-card-body__value">{{
        capitalizeFullNameClient(client())
      }}</span>
    </div>
  }

  <!-- Credit/Debit Notes Summary (Cartera cards only — fetched directly by idInvoice) -->
  @if (hasNotes()) {
    <button
      type="button"
      class="v-one-card-body__notes"
      (click)="handleNotesDetailClick()"
    >
      <v-one-icon name="purse" class="v-one-card-body__notes-icon" />
      <span class="v-one-card-body__notes-summary">
        {{ 'card.notes.label' | translate }}:
        @if (notesSummary().creditCount > 0) {
          {{ notesSummary().creditCount }}
          {{ 'card.notes.creditAbbr' | translate }}
        }
        @if (notesSummary().creditCount > 0 && notesSummary().debitCount > 0) {
          <span class="v-one-card-body__notes-separator">|</span>
        }
        @if (notesSummary().debitCount > 0) {
          {{ notesSummary().debitCount }}
          {{ 'card.notes.debitAbbr' | translate }}
        }
      </span>

      <span
        class="v-one-card-body__notes-total"
        [class.v-one-card-body__notes-total--debit]="
          notesSummary().netTotal > 0
        "
      >
        @if (notesSummary().netTotal > 0) {
          +
        }
        {{
          notesSummary().netTotal
            | currency: currency() : 'symbol-narrow' : '1.0-0'
        }}
        {{ 'card.notes.net' | translate }}
      </span>

      <span class="v-one-card-body__notes-dropdown">
        <v-one-icon
          name="chevronRight"
          class="v-one-card-body__notes-chevron"
        />
      </span>
    </button>
  }

  <!-- Total (and pending balance, when partially paid, in the same row) -->
  @if (formattedTotal() !== null || adjustedPendingAmount()) {
    <div class="v-one-card-body__totals">
      @if (formattedTotal() !== null) {
        <div class="v-one-card-body__total-item">
          <span class="v-one-card-body__total-label">{{
            'card.labels.total' | translate
          }}</span>
          @if (hasNotesAdjustment()) {
            <span class="v-one-card-body__total-original">{{
              originalTotal() | currency: currency() : 'symbol-narrow' : '1.2-2'
            }}</span>
          }
          <span class="v-one-card-body__total-value">{{
            formattedTotal() | currency: currency() : 'symbol-narrow' : '1.2-2'
          }}</span>
        </div>
      }

      @if (adjustedPendingAmount()) {
        <div
          class="v-one-card-body__total-item"
          [class.v-one-card-body__total-item--end]="isPortfolioCard()"
        >
          <span class="v-one-card-body__total-label">{{
            'card.labels.pending' | translate
          }}</span>
          <span
            class="v-one-card-body__total-value v-one-card-body__total-value--warning"
            >{{
              adjustedPendingAmount()
                | currency: currency() : 'symbol-narrow' : '1.2-2'
            }}</span
          >
        </div>
      }
    </div>
  }

  <!-- Dates -->
  @if (formattedCreationDate() || formattedDueDate()) {
    <div class="v-one-card-body__dates">
      @if (formattedCreationDate()) {
        <div class="v-one-card-body__date-item">
          <span class="v-one-card-body__date-label">{{
            'card.labels.creation' | translate
          }}</span>
          <span class="v-one-card-body__date-value">{{
            formattedCreationDate() | date: 'dd/MM/yyyy'
          }}</span>
        </div>
      }

      @if (formattedDueDate()) {
        <div
          class="v-one-card-body__date-item"
          [class.v-one-card-body__date-item--end]="isPortfolioCard()"
        >
          <span class="v-one-card-body__date-label">{{
            'card.labels.due' | translate
          }}</span>
          @if (dueDateStatus(); as status) {
            <span
              class="v-one-card-body__date-value"
              [class.v-one-card-body__date-value--warning]="
                status.variant === 'warning'
              "
              [class.v-one-card-body__date-value--danger]="
                status.variant === 'danger'
              "
            >
              <span class="v-one-card-body__date-value-wrapper">
                {{ status.message | translate: status.params }}
                @if (status.variant === 'danger') {
                  <v-one-icon
                    name="info"
                    [type]="iconType.FILLED"
                    class="v-one-card-body__overdue-icon"
                    [appTooltip]="
                      'card.dueDate.overdueTooltip'
                        | translate: { date: status.overdueDate }
                    "
                    tooltipSeverity="default"
                    [tooltipGapMobile]="8"
                  />
                }
              </span>
            </span>
          } @else {
            <span class="v-one-card-body__date-value">{{
              formattedDueDate() | date: 'dd/MM/yyyy'
            }}</span>
          }
        </div>
      }
    </div>
  }
</div>
